"use client";

import { memo, useEffect, useRef, type CSSProperties, type PointerEvent } from "react";
import { useTranslations } from "next-intl";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCountUp } from "../../hooks/useCountUp";
import type { NetworkBrand } from "./brands";

const DESKTOP_QUERY = "(min-width: 760px)";
const FOCUS_RING = "focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[3px]";

const logoMask = (src: string): CSSProperties => ({
  WebkitMaskImage: `url(${src})`,
  maskImage: `url(${src})`,
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
  WebkitMaskSize: "contain",
  maskSize: "contain",
});

const pad = (n: number) => String(n).padStart(2, "0");

interface NetworkPanelProps {
  brand: NetworkBrand;
  index: number;
  total: number;
  isActive: boolean;
  reducedMotion: boolean;
  onActivate: (index: number, moveFocus: boolean) => void;
  consumeFocusRequest: (index: number) => boolean;
}

function StatValue({ brand, reducedMotion }: { brand: NetworkBrand; reducedMotion: boolean }) {
  const value = useCountUp(brand.stat.value, { durationMs: 800, disabled: reducedMotion });
  return (
    <span className="block text-[clamp(72px,9vw,124px)] font-bold leading-[0.85] tracking-[-0.03em] tabular-nums">
      {brand.stat.prefix}
      {value}
      {brand.stat.suffix}
    </span>
  );
}

function ActiveContent({
  brand,
  index,
  total,
  reducedMotion,
  consumeFocusRequest,
}: Omit<NetworkPanelProps, "isActive" | "onActivate">) {
  const t = useTranslations("network");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (consumeFocusRequest(index)) ref.current?.focus({ preventScroll: true });
  }, [consumeFocusRequest, index]);

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="flex h-full min-w-0 animate-network-rise flex-col justify-between gap-7 p-7 outline-none motion-reduce:animate-none sm:p-9 xl:min-w-[560px]"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="text-base font-semibold tabular-nums">
          {pad(index + 1)} / {pad(total)}
        </span>
        <span className="rounded-full border-[1.5px] border-current px-3 py-1 text-sm font-semibold">
          {t(`tags.${brand.tagKey}`)}
        </span>
      </div>

      <div>
        <h3 className="sr-only">{t(brand.key)}</h3>
        <span
          aria-hidden="true"
          className="block h-[clamp(64px,6.8vw,92px)] w-[clamp(128px,13.6vw,184px)] bg-current"
          style={logoMask(brand.logo)}
        />
        <p className={cn("mt-4 max-w-[520px] text-lg leading-[1.55]", brand.theme.muted)}>
          {t(brand.descriptionKey)}
        </p>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <StatValue brand={brand} reducedMotion={reducedMotion} />
          <span className="mt-2 block text-[17px] font-medium">{t(`statLabels.${brand.statLabelKey}`)}</span>
        </div>
        <a
          href={brand.link}
          target="_blank"
          rel="noreferrer noopener"
          className={cn(
            "inline-flex items-center gap-2 rounded-full px-[26px] py-4 text-[17px] font-bold transition-transform duration-200 hover:-translate-y-0.5",
            brand.theme.button,
            FOCUS_RING,
            brand.theme.focus,
          )}
        >
          {t("link")}
          <ArrowUpRight className="h-5 w-5" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}

function NetworkPanel({
  brand,
  index,
  total,
  isActive,
  reducedMotion,
  onActivate,
  consumeFocusRequest,
}: NetworkPanelProps) {
  const t = useTranslations("network");

  const handlePointerEnter = (e: PointerEvent<HTMLDivElement>) => {
    if (isActive || e.pointerType !== "mouse") return;
    if (!window.matchMedia(DESKTOP_QUERY).matches) return;
    onActivate(index, false);
  };

  return (
    <div
      onPointerEnter={handlePointerEnter}
      aria-current={isActive ? "true" : undefined}
      className={cn(
        "relative min-w-0 flex-none overflow-hidden rounded-[24px] border-[1.5px] border-brand-navy",
        "min-[760px]:transition-[flex-grow] min-[760px]:duration-700 min-[760px]:ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none",
        isActive ? "min-[760px]:flex-[3] lg:flex-[3.4]" : "min-[760px]:flex-1",
        brand.theme.panel,
      )}
    >
      {isActive ? (
        <ActiveContent
          brand={brand}
          index={index}
          total={total}
          reducedMotion={reducedMotion}
          consumeFocusRequest={consumeFocusRequest}
        />
      ) : (
        <button
          type="button"
          onClick={() => onActivate(index, true)}
          aria-expanded={false}
          aria-label={t("open", { brand: brand.name })}
          className={cn(
            "flex h-[84px] w-full items-center gap-5 px-6 text-left min-[760px]:h-full min-[760px]:flex-col min-[760px]:items-start min-[760px]:justify-between min-[760px]:py-6",
            "focus-visible:outline focus-visible:outline-[3px] focus-visible:[outline-offset:-6px]",
            brand.theme.focus,
          )}
        >
          <span aria-hidden="true" className="text-base font-semibold tabular-nums">
            {pad(index + 1)}
          </span>
          <span
            aria-hidden="true"
            className="relative block h-[44px] w-[132px] min-[760px]:h-[150px] min-[760px]:w-[60px]"
          >
            <span
              className="block h-full w-full bg-current min-[760px]:absolute min-[760px]:left-1/2 min-[760px]:top-1/2 min-[760px]:h-[60px] min-[760px]:w-[150px] min-[760px]:-translate-x-1/2 min-[760px]:-translate-y-1/2 min-[760px]:-rotate-90"
              style={logoMask(brand.logo)}
            />
          </span>
        </button>
      )}
    </div>
  );
}

export default memo(NetworkPanel);
